import { useState, useEffect, useMemo } from "react";
import { getTodaysOperationsDataHttp, saveTodaysOperationsUpdateHttp, getOperationsHistoryHttp } from "../../../http/renewalHttp/RenewalHttp";
import Div from "../../../components/Div";
import PageTitle from "../../../components/PageTitle";
import { Badge, Pagination, Modal, Button } from 'react-bootstrap';

const getTodayISO = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

export default function GetTodaysOperations() {
    const [opsData, setOpsData] = useState([]);
    const [loading, setLoading] = useState(true);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(50);
    const [searchTerm, setSearchTerm] = useState("");

    const [startDate, setStartDate] = useState(getTodayISO());
    const [endDate, setEndDate] = useState(getTodayISO());

    const [editedRows, setEditedRows] = useState({});

    // History Modal State
    const [showHistory, setShowHistory] = useState(false);
    const [historyLogs, setHistoryLogs] = useState([]);
    const [historyDomain, setHistoryDomain] = useState("");

    const loadOpsData = async () => {
        setLoading(true);
        try {
            const payload = { start_date: startDate, end_date: endDate };
            const res = await getTodaysOperationsDataHttp(payload);
            if (res && res.data) setOpsData(res.data);
        } catch (error) {
            console.error("Error loading data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setStartDate(getTodayISO());
        setEndDate(getTodayISO());
        setSearchTerm("");
        setCurrentPage(1);
        setTimeout(() => loadOpsData(), 50); 
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { loadOpsData(); }, []);

    const sortedAndFilteredData = useMemo(() => {
        const safeSearch = (searchTerm || "").trim().toLowerCase();

        let processedList = opsData.filter(item => {
            if (!safeSearch) return true;
            return String(item.Domains || "").toLowerCase().includes(safeSearch);
        });

        return processedList.sort((a, b) => {
            if (safeSearch !== "") return String(a.Domains || "").localeCompare(String(b.Domains || ""));

            const todayISO = getTodayISO();

            const isSnoozedA = a.Actual_Date && a.shivaami_renewal_date &&
                a.Actual_Date.substring(0, 10) === todayISO && a.Actual_Date.substring(0, 10) !== a.shivaami_renewal_date.substring(0, 10);

            const isSnoozedB = b.Actual_Date && b.shivaami_renewal_date &&
                b.Actual_Date.substring(0, 10) === todayISO && b.Actual_Date.substring(0, 10) !== b.shivaami_renewal_date.substring(0, 10);

            const isProcessedA = (a.Actual_Date && a.Actual_Date.substring(0, 10) > todayISO) ||
                (!isSnoozedA && ((a.approval_status === "Approved" || a.approval_status === "Rejected") || (a.Malvika_Comments && a.Malvika_Comments.trim() !== "")));

            const isProcessedB = (b.Actual_Date && b.Actual_Date.substring(0, 10) > todayISO) ||
                (!isSnoozedB && ((b.approval_status === "Approved" || b.approval_status === "Rejected") || (b.Malvika_Comments && b.Malvika_Comments.trim() !== "")));

            if (isSnoozedA && !isSnoozedB) return -1;
            if (!isSnoozedA && isSnoozedB) return 1;
            if (isProcessedA && !isProcessedB) return 1;
            if (!isProcessedA && isProcessedB) return -1;

            const dateA = new Date(a.shivaami_renewal_date || 0);
            const dateB = new Date(b.shivaami_renewal_date || 0);
            return dateB - dateA;
        });
    }, [opsData, searchTerm]); 

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = sortedAndFilteredData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(sortedAndFilteredData.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo(0, 0);
    };

    const handleInputChange = (subscriptionId, field, value) => {
        setEditedRows(prev => ({ ...prev, [subscriptionId]: { ...prev[subscriptionId], [field]: value } }));
    };

    const handleSave = async (item) => {
        const edits = editedRows[item.subscriptionId] || {};
        const payload = {
            subscription_id: item.subscriptionId,
            domain_name: item.Domains,
            malvika_comments: edits.Malvika_Comments !== undefined ? edits.Malvika_Comments : (item.Malvika_Comments || ""),
            reprovision_date: "", 
            actual_date: edits.Actual_Date !== undefined ? edits.Actual_Date : (item.Actual_Date || ""),
            approval_status: edits.approval_status !== undefined ? edits.approval_status : (item.approval_status || "Pending"),
            email: localStorage.getItem('user')
        };

        const res = await saveTodaysOperationsUpdateHttp(payload);
        if (res) {
            alert("Saved successfully!");
            setEditedRows(prev => {
                const next = { ...prev };
                delete next[item.subscriptionId];
                return next;
            });
            loadOpsData();
        } else {
            alert("Failed to save.");
        }
    };

    const viewHistory = async (item) => {
        setHistoryDomain(item.Domains);
        const res = await getOperationsHistoryHttp(item.subscriptionId);
        if (res && res.data) {
            setHistoryLogs(res.data);
            setShowHistory(true);
        } else {
            alert("No history found or error fetching history.");
        }
    };

    return (
        <Div className="container-fluid p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <PageTitle title="Daily Operations Tasks" />
                
                <div className="d-flex gap-2 align-items-center">
                    <input type="date" className="form-control form-control-sm border-secondary" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                    <span className="fw-bold text-secondary">-</span>
                    <input type="date" className="form-control form-control-sm border-secondary" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                    <button className="btn btn-primary btn-sm px-3" onClick={loadOpsData}>Filter</button>
                    <button className="btn btn-outline-secondary btn-sm px-3" onClick={handleReset}>Reset</button>
                    <input type="text" className="form-control form-control-sm border-primary ms-3" placeholder=" Search Domain..." value={searchTerm} style={{ width: '200px' }} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
                    <Badge bg="info" className="p-2 fs-6 ms-2">Total Records: {sortedAndFilteredData.length}</Badge>
                </div>
            </div>

            <Div className="col-md-12 mt-2">
                <div className="table-responsive bg-white shadow-sm rounded p-1" style={{ maxHeight: '72vh', overflowY: 'auto' }}>
                    {loading ? (
                        <div className="text-center py-5">Loading data...</div>
                    ) : (
                        <table key="main-table" className="table table-bordered table-hover align-middle mb-0">
                            <thead className="bg-primary text-white sticky-top" style={{ fontSize: '11px', zIndex: 10 }}>
                                <tr>
                                    <th>#</th>
                                    <th>Renewal Date (SRD)</th>
                                    <th>Domain</th>
                                    <th>Plan</th>
                                    <th>Licenses</th>
                                    <th>Renewed Users</th>
                                    <th>Status</th>
                                    <th>Google Date (GRD)</th>
                                    <th>Team</th>
                                    <th>Special Comment</th>
                                    <th>Ops Status</th>
                                    <th>Malvika Comments</th>
                                    <th>GRD Cases</th>
                                    <th>Google Renewal GMT</th>
                                    <th>Action</th>
                                    <th>History</th>                                
                                </tr>
                            </thead>
                            <tbody style={{ fontSize: '11px' }}>
                                {currentItems.length > 0 ? currentItems.map((item, index) => {
                                    const rowEdits = editedRows[item.subscriptionId] || {};

                                    const isSnoozed = item.Actual_Date && item.shivaami_renewal_date &&
                                        item.Actual_Date.substring(0, 10) === getTodayISO() && item.Actual_Date.substring(0, 10) !== item.shivaami_renewal_date.substring(0, 10);

                                    const isProcessed = (item.Actual_Date && item.Actual_Date.substring(0, 10) > getTodayISO()) ||
                                        (!isSnoozed && ((item.approval_status === "Approved" || item.approval_status === "Rejected") || (item.Malvika_Comments && item.Malvika_Comments.trim() !== "")));

                                    let rowClass = "";
                                    if (isSnoozed) rowClass = "table-warning";
                                    else if (isProcessed) rowClass = "table-light";

                                    return (
                                        <tr key={`main-${item.Domains}-${item.Plan}`} className={rowClass}>
                                            <td>
                                                {indexOfFirstItem + index + 1}
                                                {isSnoozed && <div style={{ fontSize: '9px', color: '#856404', marginTop: '4px', fontWeight: 'bold' }}>SNOOZED</div>}
                                            </td>

                                            <td>{item.shivaami_renewal_date || '--'}</td>
                                            <td className="fw-bold text-primary">{item.Domains}</td>
                                            <td>{item.Plan}</td>
                                            <td>{item.Licenses}</td>
                                            <td>{item.Renewed_Users || '--'}</td>
                                            <td><Badge bg="secondary" style={{ color: '#000000' }}>{item.Renewal_Status || '--'}</Badge></td>
                                            <td>{item.Google_renewal_date || '--'}</td>
                                            <td>{item.Renewal_Team || '--'}</td>
                                            <td>{item.Special_Comment || '--'}</td>

                                            <td>
                                                <select
                                                    className={`form-select form-select-sm fw-bold ${
                                                        (rowEdits.approval_status !== undefined ? rowEdits.approval_status : item.approval_status) === 'Approved' ? 'text-success' :
                                                        (rowEdits.approval_status !== undefined ? rowEdits.approval_status : item.approval_status) === 'Rejected' ? 'text-danger' : 'text-secondary'
                                                    }`}
                                                    value={rowEdits.approval_status !== undefined ? rowEdits.approval_status : (item.approval_status || 'Pending')}
                                                    onChange={(e) => handleInputChange(item.subscriptionId, 'approval_status', e.target.value)}
                                                >
                                                    <option value="Pending">Pending</option>
                                                    <option value="Approved">Approved</option>
                                                    <option value="Rejected">Rejected</option>
                                                </select>
                                            </td>

                                            <td>
                                                <input
                                                    className="form-control form-control-sm"
                                                    type="text"
                                                    value={rowEdits.Malvika_Comments !== undefined ? rowEdits.Malvika_Comments : (item.Malvika_Comments || '')}
                                                    onChange={(e) => handleInputChange(item.subscriptionId, 'Malvika_Comments', e.target.value)}
                                                    placeholder="Ops comment..."
                                                />
                                            </td>
                                            
                                            <td>
                                                <input
                                                    className="form-control form-control-sm"
                                                    type="date"
                                                    value={rowEdits.Actual_Date !== undefined ? rowEdits.Actual_Date : (item.Actual_Date ? item.Actual_Date.substring(0, 10) : '')}
                                                    onChange={(e) => handleInputChange(item.subscriptionId, 'Actual_Date', e.target.value)}
                                                />
                                            </td>

                                            <td className="fw-bold">{item.Renewal_GMT || '--'}</td>

                                            <td><button className="btn btn-success btn-sm w-100" onClick={() => handleSave(item)}>Save</button></td>

                                            <td>
                                                <button className="btn btn-outline-info btn-sm w-100" onClick={() => viewHistory(item)}>
                                                    <i className="fa fa-history"></i> View
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                }) : (
                                    <tr><td colSpan="16" className="text-center py-4">No tasks found for selected dates.</td></tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                {totalPages > 1 && (
                    <div className="d-flex justify-content-center mt-3">
                        <Pagination size="sm">
                            <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                            {[...Array(totalPages)].map((_, i) => (
                                <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => handlePageChange(i + 1)}>{i + 1}</Pagination.Item>
                            ))}
                            <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                        </Pagination>
                    </div>
                )}
            </Div>

            {/* HISTORY MODAL */}
            <Modal show={showHistory} onHide={() => setShowHistory(false)} size="lg">
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="fs-5">Operations History: <strong>{historyDomain}</strong></Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {historyLogs.length > 0 ? (
                        <div className="table-responsive">
                            <table className="table table-bordered table-striped" style={{ fontSize: '12px' }}>
                                <thead style={{ backgroundColor: '#212529' }}>
                                    <tr>
                                        <th style={{ color: '#100e0e' }}>Date/Time</th>
                                        <th style={{ color: '#100e0e' }}>Status</th>
                                        <th style={{ color: '#100e0e' }}>Comments</th>
                                        <th style={{ color: '#100e0e' }}>GRD Case (Actual Date)</th>
                                        <th style={{ color: '#100e0e' }}>Updated By</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {historyLogs.map((log, i) => (
                                        <tr key={i}>
                                            <td>{new Date(log.updated_at).toLocaleString()}</td>
                                            <td>
                                                <span className={`badge ${log.approval_status === 'Approved' ? 'bg-success' : log.approval_status === 'Rejected' ? 'bg-danger' : 'bg-secondary'}`}>
                                                    {log.approval_status || 'Pending'}
                                                </span>
                                            </td>
                                            <td>{log.malvika_comments || '--'}</td>
                                            <td>{log.actual_date ? String(log.actual_date).substring(0, 10) : '--'}</td>
                                            <td>{log.updated_by}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-4 text-muted">No history logs found.</div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowHistory(false)}>Close</Button>
                </Modal.Footer>
            </Modal>

        </Div>
    );
}